import type { StrategyNode } from './BaseNode';
import { v4 as uuid } from 'uuid';
import { isStrategyNode } from './typeGuards';
import { RegressionNode } from './RegressionNode';
import { reinstantiate } from '../utils/nodeFactory';

/**
 * MeepMeepNode: Canonical top-level node for microstructure strategies.
 * Aligned with backend contract defined in docs/meepmeepintegration/meepmeep_top_level_node.md
 */
export class MeepMeepNode implements StrategyNode {
    id: string;
    type: 'MeepMeep' = 'MeepMeep';
    title?: string;
    isManualRename?: boolean;
    isTopLevel = true;
    isMicrostructure = true;
    isPayloadAware = true;
    config: {
        main_market?: StrategyNode;
        signal?: StrategyNode;
        latency_ms: number;
        insample_days: number;
        target_forward_ticks: number;
        max_volume: number;
        trade_margin: number;
        trade_margin_regime: string;
    };

    constructor(
        id: string,
        title?: string,
        main_market?: StrategyNode,
        signal?: StrategyNode,
        latency_ms: number = 35,
        insample_days: number = 3,
        target_forward_ticks: number = 25,
        max_volume: number = 1,
        trade_margin: number = 0,
        trade_margin_regime: string = 'static',
        isManualRename?: boolean
    ) {
        this.id = id;
        this.title = title || 'MeepMeep Strategy';
        this.isManualRename = isManualRename;

        const finalSignal = signal ? reinstantiate(signal) : new RegressionNode(uuid(), 'Alpha Signal');

        // Re-instantiate main_market if it's a raw object
        const finalMain = main_market ? reinstantiate(main_market) : undefined;

        this.config = {
            main_market: finalMain,
            signal: finalSignal,
            latency_ms,
            insample_days,
            target_forward_ticks,
            max_volume,
            trade_margin,
            trade_margin_regime,
        };
    }

    update(id: string, patch: Partial<StrategyNode>): StrategyNode {
        if (this.id === id) {
            const mergedConfig = { ...this.config, ...(patch.config || {}) };

            return new MeepMeepNode(
                this.id,
                patch.title || this.title,
                mergedConfig.main_market,
                mergedConfig.signal,
                mergedConfig.latency_ms,
                mergedConfig.insample_days,
                mergedConfig.target_forward_ticks,
                mergedConfig.max_volume,
                mergedConfig.trade_margin,
                mergedConfig.trade_margin_regime,
                patch.isManualRename ?? this.isManualRename
            );
        }

        // Recursively update children
        const updatedMain = this.config.main_market ? this.config.main_market.update(id, patch) : undefined;
        const updatedSignal = this.config.signal ? this.config.signal.update(id, patch) : undefined;

        if (updatedMain !== this.config.main_market || updatedSignal !== this.config.signal) {
            return new MeepMeepNode(
                this.id,
                this.title,
                updatedMain,
                updatedSignal,
                this.config.latency_ms,
                this.config.insample_days,
                this.config.target_forward_ticks,
                this.config.max_volume,
                this.config.trade_margin,
                this.config.trade_margin_regime,
                this.isManualRename
            );
        }

        return this;
    }

    clone(): StrategyNode {
        return new MeepMeepNode(
            uuid(),
            this.title,
            isStrategyNode(this.config.main_market) ? this.config.main_market.clone() : undefined,
            isStrategyNode(this.config.signal) ? this.config.signal.clone() : undefined,
            this.config.latency_ms,
            this.config.insample_days,
            this.config.target_forward_ticks,
            this.config.max_volume,
            this.config.trade_margin,
            this.config.trade_margin_regime,
            this.isManualRename
        );
    }
}
